import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';
import cx from 'classnames';

import Checkbox from '@ontruck/ontruck-react-components/lib/Checkbox';

import {
  Table as VirtualTable,
  Column,
  AutoSizer,
  InfiniteLoader,
  defaultTableRowRenderer,
} from 'react-virtualized';

import { Loading } from 'components/Loading';

import style from './style.module.scss';

const t = {
  not_added: 'No area',
};

const LoadingRow = ({ text, ...props }) => (
  <div className={style.loadingRow} {...props}>
    <Loading size={20} /> {text}
  </div>
);

LoadingRow.propTypes = {
  text: PropTypes.string,
};

class OutcodeListContainer extends Component {
  constructor(props) {
    super(props);
    this.rowGetter = this.rowGetter.bind(this);
    this.isRowLoaded = this.isRowLoaded.bind(this);
    this.loadMoreRows = this.loadMoreRows.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.checkboxCellRenderer = this.checkboxCellRenderer.bind(this);
    this.checkboxHeaderRenderer = this.checkboxHeaderRenderer.bind(this);
    this.isRowSelected = this.isRowSelected.bind(this);
    this.handleSelectAllClick = this.handleSelectAllClick.bind(this);
  }

  rowGetter({ index }) {
    const { outcodes } = this.props;
    return outcodes[index];
  }

  rowRenderer({ index, key, className, rowData, ...rest }) {
    const { intl } = this.props;

    const t = intl.messages.components.OutcodeList;

    const isRowSelected = this.isRowSelected(rowData);

    const row = this.isRowLoaded({ index }) ? (
      defaultTableRowRenderer({
        index,
        key,
        style: rest.style,
        className: cx(className, {
          [style.rowSelected]: isRowSelected,
        }),
        rowData,
        ...rest,
      })
    ) : (
      <LoadingRow key={key} style={rest.style} text={t.loading} />
    );

    return row;
  }

  statusRenderer({ cellData, rowData, rowIndex }) {
    if (rowData.not_added) {
      return t.not_added;
    }

    return (
      <div
        className={cx(style.status, {
          [style.active]: cellData,
          [style.inactive]: !cellData,
        })}
      />
    );
  }

  isRowSelected(rowData) {
    const { selectedOutcodes } = this.props;
    return (
      selectedOutcodes.length &&
      selectedOutcodes.some(({ outcode } = {}) => outcode === rowData.outcode)
    );
  }

  isRowLoaded({ index }) {
    const { outcodes, hasMoreOutcodes } = this.props;
    return !hasMoreOutcodes || index < outcodes.length - 1;
  }

  loadMoreRows() {
    const { onLoadMoreRows } = this.props;
    onLoadMoreRows();
  }

  handleCheckboxRowClick(rowData) {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();

      const { onRowClick } = this.props;
      onRowClick({ rowData });
    };
  }

  handleSelectAllClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onSelectAllClick } = this.props;

    onSelectAllClick();
  }

  checkboxCellRenderer({ rowData }) {
    const checked = !!this.isRowSelected(rowData);

    return <Checkbox checked={checked} onClick={this.handleCheckboxRowClick(rowData)} />;
  }

  checkboxHeaderRenderer() {
    const { selectAllEnabled, selectedOutcodes, outcodes } = this.props;

    return selectAllEnabled ? (
      <Checkbox
        checked={selectedOutcodes.length === outcodes.length}
        onClick={this.handleSelectAllClick}
      />
    ) : (
      'Max 200'
    );
  }

  render() {
    const { outcodes, totalOutcodes, threshold, intl, onRowClick } = this.props;

    const t = intl.messages.components.OutcodeList;

    return (
      <div className={style.table}>
        <InfiniteLoader
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          rowCount={totalOutcodes}
          threshold={threshold}
        >
          {({ onRowsRendered }) => (
            <AutoSizer>
              {({ width, height }) => (
                <VirtualTable
                  height={height}
                  width={width}
                  headerHeight={30}
                  rowHeight={40}
                  rowCount={outcodes.length}
                  rowGetter={this.rowGetter}
                  rowClassName={style.row}
                  headerClassName={style.header}
                  headerRowRenderer={({ columns, className, ...props }) => (
                    <div className={style.tableRow} role="row" style={props.style}>
                      {columns}
                    </div>
                  )}
                  onRowsRendered={onRowsRendered}
                  onRowClick={onRowClick}
                  rowRenderer={this.rowRenderer}
                  overscanRowCount={50}
                >
                  <Column
                    dataKey={''}
                    width={30}
                    cellRenderer={this.checkboxCellRenderer}
                    headerRenderer={this.checkboxHeaderRenderer}
                  />
                  <Column
                    className={style.bold}
                    dataKey="district"
                    label={t.district}
                    width={100}
                  />
                  <Column dataKey="town" label={t.town} width={300} />
                  <Column
                    dataKey="status"
                    label={t.status}
                    width={90}
                    cellRenderer={this.statusRenderer}
                  />
                </VirtualTable>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
    );
  }
}

OutcodeListContainer.propTypes = {
  outcodes: PropTypes.array,
  totalOutcodes: PropTypes.number,
  onLoadMoreRows: PropTypes.func,
  onRowClick: PropTypes.func,
  hasMoreOutcodes: PropTypes.bool,
  threshold: PropTypes.number,
  selectedOutcodes: PropTypes.array,
  intl: PropTypes.object,
  selectAllEnabled: PropTypes.bool,
  onSelectAllClick: PropTypes.func,
};

OutcodeListContainer.defaultProps = {};

export default injectIntl(OutcodeListContainer);
